import { cloneElement, useEffect, useState, type ReactElement } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_SERVICES, type Project, type ProjectCreate, type ProjectServicesConfig } from '@one-deployment/api'
import { Button, Dialog, Input, Label, Select, SkeletonRows } from '@/components/ui'
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group'
import { ErrorState } from '@/components/status'
import { enc, post, put } from '@/lib/api'
import { cn } from '@/lib/cn'
import { qk } from '@/lib/query'
import { InstallLink, useIntegrations } from './Settings'
import { ServicesForm } from './Services'
import { NewFromFile, NewFromTemplate } from './NewFromTemplate'

const STEPS = ['Name', 'Repository', 'Linear', 'Services', 'Containers', 'Secrets'] as const
const KEY = /^[a-z][a-z0-9-]{1,31}$/

/**
 * Create a project (03 §5.11): name, GitHub repo, Linear team, services
 * (hosting, jobs, database, mobile, each on a chosen account), container
 * limits, first secrets. Secrets are stored encrypted; a lead can view
 * them in Settings → Secrets (each view is audited).
 */
export function CreateProjectButton({ trigger }: { trigger: ReactElement<{ onClick?: () => void }> }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      {cloneElement(trigger, { onClick: () => setOpen(true) })}
      {open && <Wizard onClose={() => setOpen(false)} />}
    </>
  )
}

function Wizard({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(0)
  // New: made from main-template by the setup flow (spec 06). Existing: a repo that is already there.
  const [source, setSource] = useState<'template' | 'existing' | 'file'>('template')
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [repoId, setRepoId] = useState<number | null>(null)
  // Token mode: a client org the App is not installed on.
  const [auth, setAuth] = useState<'app' | 'token'>('app')
  const [gh, setGh] = useState({ owner: '', name: '', token: '', user: '' })
  const [team, setTeam] = useState<string | null>(null)
  const [services, setServices] = useState<ProjectServicesConfig>(DEFAULT_SERVICES)
  const [memGb, setRam] = useState(16)
  const [maxContainers, setMax] = useState(1)
  const [secrets, setSecrets] = useState<{ name: string; value: string }[]>([{ name: '', value: '' }])
  const nav = useNavigate()
  const qc = useQueryClient()

  const ints = useIntegrations(step === 1 || step === 2)
  // No App yet: a token is the only way in, so don't open on a dead end.
  useEffect(() => { if (ints.data && !ints.data.github.appReady) setAuth('token') }, [ints.data])
  const create = useMutation({
    mutationFn: async () => {
      const body: ProjectCreate = {
        name, key, linearTeamId: team ?? undefined,
        ...(auth === 'app' ? { repoId: repoId! } : { githubToken: { owner: gh.owner.trim(), name: gh.name.trim(), token: gh.token, user: gh.user.trim() || undefined } }),
        settings: { containerMemMb: memGb * 1024, maxContainers },
        services,
      }
      const created = await post<Project>('/api/projects', body)
      // Secrets are their own write-only resource: set one by one once the project exists.
      for (const s of secrets.filter((x) => x.name && x.value)) {
        await put(`/api/projects/${enc(created.key)}/secrets/${enc(s.name)}`, { value: s.value })
      }
      return created
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: qk.overview() })
      await qc.invalidateQueries({ queryKey: qk.projects() })
      onClose()
      void nav({ to: '/p/$project', params: { project: r.key } })
    },
  })

  const ok = [name.trim() && KEY.test(key), auth === 'app' ? repoId != null : !!(gh.owner.trim() && gh.name.trim() && gh.token.length >= 20), true, true, memGb > 0 && maxContainers > 0, true][step]

  if (source === 'template' && step > 0) return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Create project" description="New from main-template" width={640}>
      <NewFromTemplate name={name} projectKey={key} onBack={() => setStep(0)} onClose={onClose} />
    </Dialog>
  )

  if (source === 'file') return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Create project" description="From a project file" width={640}>
      <NewFromFile onBack={() => setSource('template')} onClose={onClose} />
    </Dialog>
  )

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Create project" description={`Step ${step + 1} of ${STEPS.length} — ${STEPS[step]}`} width={640}>
      <ol className="mb-4 flex gap-1">{STEPS.map((s, i) => <li key={s} className={cn('h-1 flex-1 rounded-full', i <= step ? 'bg-accent' : 'bg-surface-3')} aria-label={s} />)}</ol>
      <div className="min-h-48 space-y-3">
        {step === 0 && <>
          <RadioGroup aria-label="Source" value={source} onValueChange={(v) => setSource(v as typeof source)} className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><RadioGroupItem value="template" />New from main-template</label>
            <label className="flex items-center gap-2"><RadioGroupItem value="existing" />Existing repo</label>
            <label className="flex items-center gap-2"><RadioGroupItem value="file" />From a project file</label>
          </RadioGroup>
          <div><Label htmlFor="cp-name">Name</Label><Input id="cp-name" autoFocus value={name} onChange={(e) => { setName(e.target.value); if (!key || key === slug(name)) setKey(slug(e.target.value)) }} /></div>
          <div><Label htmlFor="cp-key">Key (in URLs and container names; cannot change)</Label><Input id="cp-key" className="font-mono" value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} />
            {key && !KEY.test(key) && <p className="mt-1 text-xs text-warn">2–32 characters: lowercase letters, digits, dashes; starts with a letter.</p>}</div>
        </>}
        {step === 1 && ints.data && !ints.data.github.appReady && (
          <p className="text-xs text-fg-subtle">The TriSof GitHub App is not set up yet, so projects connect with a personal access token.</p>
        )}
        {step === 1 && ints.data?.github.appReady !== false && (
          <RadioGroup aria-label="Connect with" value={auth} onValueChange={(v) => setAuth(v as typeof auth)} className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><RadioGroupItem value="app" />GitHub App</label>
            <label className="flex items-center gap-2"><RadioGroupItem value="token" />Personal access token</label>
          </RadioGroup>
        )}
        {step === 1 && auth === 'token' && <>
          <div className="flex gap-2">
            <div className="flex-1"><Label htmlFor="cp-owner">Owner</Label><Input id="cp-owner" className="font-mono" value={gh.owner} onChange={(e) => setGh({ ...gh, owner: e.target.value })} /></div>
            <div className="flex-1"><Label htmlFor="cp-repo">Repository</Label><Input id="cp-repo" className="font-mono" value={gh.name} onChange={(e) => setGh({ ...gh, name: e.target.value })} /></div>
          </div>
          <div><Label htmlFor="cp-user">GitHub user the token belongs to</Label><Input id="cp-user" className="font-mono" value={gh.user} onChange={(e) => setGh({ ...gh, user: e.target.value })} /></div>
          <div><Label htmlFor="cp-token">Token (needs push and pull-request rights)</Label><Input id="cp-token" type="password" autoComplete="off" className="font-mono" value={gh.token} onChange={(e) => setGh({ ...gh, token: e.target.value })} /></div>
          <p className="text-xs text-fg-subtle">For a repo the App cannot be installed on. The token is checked against GitHub now and stored write-only; it never reaches the container. Without the App there are no webhooks: merges are polled and CI checks are not shown.</p>
        </>}
        {step === 1 && auth === 'app' && (ints.error ? <ErrorState error={ints.error} compact retry={() => void ints.refetch()} /> : !ints.data ? <SkeletonRows rows={4} /> : ints.data.github.repos.filter((r) => !r.linkedProject).length === 0 ? (
          <p className="text-sm text-fg-muted">The TriSof GitHub App can see no repo that is not already a project. <InstallLink kind="github">Install it on your org or grant it a repo</InstallLink>, then come back.</p>
        ) : (
          <RadioGroup aria-label="Repository" value={repoId === null ? '' : String(repoId)} onValueChange={(v) => setRepoId(Number(v))} className="max-h-64 gap-0 space-y-3 overflow-auto">
            {ints.data.github.installations.map((i) => {
              const repos = ints.data!.github.repos.filter((r) => r.installationId === i.id && !r.linkedProject)
              if (!repos.length) return null
              return (
                <div key={i.id}>
                  <div className="mb-1 text-xs text-fg-subtle">{i.account}{i.suspended && ' (suspended)'}</div>
                  {repos.map((r) => (
                    <label key={r.id} className="flex h-7 cursor-pointer items-center gap-2 text-sm"><RadioGroupItem value={String(r.id)} />{r.owner}/{r.name}</label>
                  ))}
                </div>
              )
            })}
            <p className="text-xs text-fg-subtle">Repo not listed? Grant the app access to it on GitHub.</p>
          </RadioGroup>
        ))}
        {step === 2 && (ints.error ? <ErrorState error={ints.error} compact /> : !ints.data ? <SkeletonRows rows={3} /> : (
          <div><Label htmlFor="cp-team">Linear team (optional — connect later from settings)</Label>
            <Select id="cp-team" value={team ?? ''} onChange={(e) => setTeam(e.target.value || null)} className="w-full">
              <option value="">None for now</option>
              {ints.data.linear.orgs.flatMap((o) => o.teams.map((t) => <option key={t.id} value={t.id}>{o.name} · {t.key} — {t.name}</option>))}
            </Select></div>
        ))}
        {step === 3 && <ServicesForm value={services} onChange={setServices} />}
        {step === 4 && <>
          <div><Label htmlFor="cp-ram">RAM per container (GB)</Label><Input id="cp-ram" type="number" min={2} value={memGb} onChange={(e) => setRam(Number(e.target.value))} /></div>
          <div><Label htmlFor="cp-max">Max containers</Label><Input id="cp-max" type="number" min={1} value={maxContainers} onChange={(e) => setMax(Number(e.target.value))} /></div>
        </>}
        {step === 5 && <>
          <p className="text-xs text-fg-subtle">Environment for the project's container (UAT credentials, API keys). Write-only: nobody can read them back here, you included.</p>
          {secrets.map((s, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="NAME" className="w-48 font-mono" value={s.name} onChange={(e) => setSecrets(secrets.map((x, j) => (j === i ? { ...x, name: e.target.value.toUpperCase() } : x)))} />
              <Input placeholder="value" type="password" autoComplete="off" value={s.value} onChange={(e) => setSecrets(secrets.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
            </div>
          ))}
          <Button size="sm" onClick={() => setSecrets([...secrets, { name: '', value: '' }])}>Add another</Button>
        </>}
      </div>
      {create.error && <div className="mt-3"><ErrorState error={create.error} compact /></div>}
      <div className="mt-4 flex justify-between">
        <Button onClick={() => (step === 0 ? onClose() : setStep(step - 1))}>{step === 0 ? 'Cancel' : 'Back'}</Button>
        {step < STEPS.length - 1 ? <Button variant="primary" disabled={!ok} onClick={() => setStep(step + 1)}>Next</Button>
          : <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>Create project</Button>}
      </div>
    </Dialog>
  )
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32)
}
